// frontend/src/App.js - النسخة المحسّنة مع Code Splitting

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Outlet, useParams, useNavigate, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { HelmetProvider } from 'react-helmet-async';
import './styles/responsive.css';
import { logger } from './utils/logger';
import { SecureStorage } from './utils/secureStorage';
// في أعلى الملف مع باقي الـ imports
import ParticlesNetwork from './components/ParticlesNetwork';
// Context
import { LanguageProvider } from './context/LanguageContext';
//import ProtectedRoute from './components/ProtectedRoute';
// ✅ استيراد Hook تتبع الزوار
import { useVisitorTracker } from './hooks/useVisitorTracker';

// ✅ استيراد أدوات المصادقة
import { getStoredToken } from './utils/auth';
import { jwtDecode } from 'jwt-decode';

// ✅ المكونات الأساسية فقط (يتم تحميلها مباشرة)
import StickyHeader from './components/StickyHeader';
import LanguageToggle from './components/LanguageToggle';

// ✅ Lazy Loading للصفحات (يتم تحميلها عند الحاجة فقط)
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const Privacy = lazy(() => import('./components/Privacy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const DataDeletion = lazy(() => import('./components/DataDeletion'));
const GettingStarted = lazy(() => import('./pages/GettingStarted'));
const FreeTrial = lazy(() => import('./pages/FreeTrial'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SubscriptionDetails = lazy(() => import('./components/SubscriptionDetails'));
//const ElevenLabsChat = lazy(() => import('./components/ElevenLabsChat'));

// Client Routes
const ClientDashboard = lazy(() => import('./components/features/dashboard/ClientDashboard'));

const ClientLogin = lazy(() => import('./components/ClientLogin'));
const PasswordResetForm = lazy(() => import('./components/PasswordResetForm'));
const SubscriptionSuccess = lazy(() => import('./components/SubscriptionSuccess'));
const OfferPage = lazy(() => import('./components/OfferPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatWidget = lazy(() => import('./components/ChatWidget'));


// Integrations
const MetaCallback = lazy(() => import('./pages/MetaCallback'));
const MetaRedirectHandler = lazy(() => import('./pages/MetaRedirectHandler'));
const OAuthCallbackHandler = lazy(() => import('./components/OAuthCallbackHandler'));
const IntegrationSuccess = lazy(() => import('./pages/IntegrationSuccess'));

// Custom Offers
const CustomOffersManagement = lazy(() => import('./pages/CustomOffersManagement'));
const CustomOfferForm = lazy(() => import('./pages/CustomOfferForm'));
const CustomOfferPage = lazy(() => import('./pages/CustomOfferPage'));

const CohortOfferPage = lazy(() => import('./pages/CohortOfferPage'));
const CohortManagement = lazy(() => import('./pages/CohortManagement'));
const CohortSeatsManager = lazy(() => import('./pages/CohortSeatsManager'));
const CohortOfferForm = lazy(() => import('./pages/CohortForm'));

// Admin Routes
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminLayout = lazy(() => import('./components/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const PackageManagement = lazy(() => import('./pages/PackageManagement'));
const SubscribersList = lazy(() => import('./pages/SubscribersList'));
const AdminManagement = lazy(() => import('./pages/AdminManagement'));
const RolesManagement = lazy(() => import('./pages/RolesManagement'));
const AgentsManagement = lazy(() => import('./pages/AgentsManagement'));
const AdminAgentApplicationsPage = lazy(() => import('./pages/AdminAgentApplicationsPage'));
const AgentManagementPage = lazy(() => import('./pages/AgentManagementPage'));
const FinancialManagementPage = lazy(() => import('./pages/FinancialManagementPage'));
const SubscriptionAnalyticsPage = lazy(() => import('./pages/SubscriptionAnalyticsPage'));
const AgentPayoutsPage = lazy(() => import('./pages/AgentPayoutsPage'));
const PayoutRequestsPage = lazy(() => import('./pages/PayoutRequestsPage'));
const MonthlyReportsPage = lazy(() => import('./pages/MonthlyReportsPage'));
const AgentPerformancePage = lazy(() => import('./pages/AgentPerformancePage'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const OnlineUsers = lazy(() => import('./pages/OnlineUsers'));
const AdminLeadTrackingPage = lazy(() => import('./pages/AdminLeadTrackingPage'));

// Agent Routes
const AgentLayout = lazy(() => import('./components/AgentLayout').then(m => ({ default: m.AgentLayout })));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const AgentLogin = lazy(() => import('./pages/AgentLogin'));
const AgentClientManagement = lazy(() => import('./pages/AgentClientManagement'));
const AgentLeads = lazy(() => import('./pages/AgentLeads'));
const AgentEarningsPage = lazy(() => import('./pages/AgentEarningsPage'));
// مع باقي الـ Agent lazy imports
const AgentOfferManagement = lazy(() => import('./pages/AgentOfferManagement'));
const JoinAgentPage = lazy(() => import('./pages/JoinAgentPage'));


const LoadingFallback = () => {
  const [displayText, setDisplayText] = React.useState('');
  const fullText = 'هايبركونيكت\nأسرع . أذكى . أفضل قيمة';

  React.useEffect(() => {
    const chars = Array.from(fullText); // ← انقلها إلى داخل useEffect
    let index = 0;
    const interval = setInterval(() => {
      if (index < chars.length) {
        setDisplayText(chars.slice(0, index + 1).join(''));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 60);
    return () => clearInterval(interval);
  }, []); // ← الـ array فاضي لأن fullText ثابت

  return (
    <div style={{
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#0f172a',
      overflow: 'hidden'
    }}>
      <ParticlesNetwork />
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', fontFamily: 'Cairo, sans-serif' }}>
        {displayText.split('\n').map((line, i) => (
          <div key={i} style={{
            color: i === 0 ? '#ffffff' : '#3bdfc4',
            fontSize: i === 0 ? '2.2rem' : '1.1rem',
            fontWeight: i === 0 ? 'bold' : 'normal',
            letterSpacing: i === 0 ? '2px' : '4px',
            marginBottom: i === 0 ? '0.5rem' : '0',
            minHeight: i === 0 ? '2.5rem' : '1.5rem'
          }}>
            {line}
          </div>
        ))}
        <div style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'center',
          gap: '8px'
        }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#3bdfc4',
              animation: `bounce 1.2s ${i * 0.2}s infinite ease-in-out`
            }} />
          ))}
        </div>
        <style>{`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); opacity: 0.4; }
            50% { transform: translateY(-10px); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
};

const AppWrapper = styled.div`
  direction: ${props => props.isArabic ? 'rtl' : 'ltr'};
`;

const LogoLink = styled.a`
  position: fixed;
  top: 15px;
  left: 15px;
  width: 60px;
  height: 60px;
  background-image: url('/icons/logo.webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 10000;
  border-radius: 50%;
  cursor: pointer;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  /* ✅ تغيير chat-open إلى ai-widget-open-mobile */
  @media (max-width: 768px) {
    body.ai-widget-open-mobile & {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }
`;

const LanguageToggleWrapper = styled.div`
  transition: opacity 0.3s ease, visibility 0.3s ease;

  body.chat-open & {
    @media (max-width: 768px) {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }
`;

const ConditionalElevenLabsChat = ({ agentId }) => {
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();

    const handleResize = () => checkIfMobile();
    const handleScroll = () => {
      if (!isMobile) return;

      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      setIsNearFooter(distanceFromBottom < 300);
    };

    window.addEventListener('resize', handleResize);
    if (isMobile) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  if (isMobile && isNearFooter) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      {/*<ElevenLabsChat agentId={agentId} />*/}
    </Suspense>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const CountryAwareLayout = () => {
  const { countryCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const validCountryCodes = ['sa', 'ae', 'eg', 'jo', 'kw', 'bh', 'qa', 'om'];

    if (countryCode && !validCountryCodes.includes(countryCode.toLowerCase())) {
      navigate('/404', { replace: true });
      return;
    }

    if (countryCode && /^[a-zA-Z]{2}$/.test(countryCode)) {
      sessionStorage.setItem('affiliateCountryCode', countryCode.toLowerCase());
    } else {
      sessionStorage.removeItem('affiliateCountryCode');
    }

    const params = new URLSearchParams(location.search);
    const agentId = params.get('agent_id');

    if (agentId) {
      sessionStorage.setItem('userSource', `agent_${agentId}`);
    }
  }, [countryCode, navigate, location.search]);

  return <Outlet />;
};

const VisitorTrackerWrapper = ({ children }) => {
  const [clientInfo, setClientInfo] = useState({ clientId: null, clientName: null });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const fetchClientInfo = () => {
      try {
        const userRole = localStorage.getItem('userRole');

        if (userRole === 'admin') {
          logger.log('ℹ️ [VisitorTracker] Admin user detected - skipping tracking');
          setClientInfo({ clientId: null, clientName: null });
          setIsReady(false);
          return;
        }

        const sessionClientData = SecureStorage.getItem('clientData', sessionStorage);

        if (sessionClientData) {
          try {
            const clientData = JSON.parse(sessionClientData);

            if (clientData.id && clientData.name) {
              setClientInfo({
                clientId: clientData.id,
                clientName: clientData.name
              });

              logger.log('✅ [VisitorTracker] Client info loaded from sessionStorage:', {
                clientId: clientData.id,
                clientName: clientData.name
              });

              setIsReady(true);
              return;
            }
          } catch (e) {
            logger.error('❌ Error parsing sessionStorage clientData:', e);
          }
        }

        const localClientData = SecureStorage.getItem('clientData', localStorage)

        if (localClientData) {
          try {
            const clientData = JSON.parse(localClientData);

            if (clientData.id && clientData.name) {
              setClientInfo({
                clientId: clientData.id,
                clientName: clientData.name
              });

              setIsReady(true);
              return;
            }
          } catch (e) {
            logger.error('❌ Error parsing localStorage clientData:', e);
          }
        }

        if (userRole === 'client') {
          const token = getStoredToken('client');

          if (token) {
            try {
              const decoded = jwtDecode(token);

              setClientInfo({
                clientId: parseInt(decoded.sub),
                clientName: null
              });

              setIsReady(true);
              return;
            } catch (decodeError) {
              logger.warn('⚠️ Could not decode client token:', decodeError);
            }
          }
        }

        setClientInfo({ clientId: null, clientName: null });
        setIsReady(true);

      } catch (error) {
        logger.error('❌ [VisitorTracker] Error fetching client info:', error);
        setClientInfo({ clientId: null, clientName: null });
        setIsReady(true);
      }
    };

    fetchClientInfo();

    const handleStorageChange = (e) => {
      if (e.key === 'userRole' || e.key === 'clientData' || e.key === 'access_token') {
        setIsReady(false);
        setTimeout(fetchClientInfo, 50);
      }
    };

    const handleLocationChange = () => {
      fetchClientInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('popstate', handleLocationChange);

    const interval = setInterval(() => {
      const currentClientId = clientInfo.clientId;
      const sessionData = SecureStorage.getItem('clientData', sessionStorage);
      const userRole = localStorage.getItem('userRole');

      if (userRole === 'admin' && isReady) {
        setIsReady(false);
        return;
      }

      if (sessionData) {
        try {
          const data = JSON.parse(sessionData);
          if (data.id && data.id !== currentClientId) {
            fetchClientInfo();
          }
        } catch (e) {
          // ignore
        }
      }
    }, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(interval);
    };
  }, [clientInfo.clientId, isReady]);

  useVisitorTracker({
    clientId: clientInfo.clientId,
    clientName: clientInfo.clientName,
    enabled: isReady
  });

  return <>{children}</>;
};

/**
 * ✅ مكون عالمي لالتقاط معلمات التتبع من الرابط وحفظها في sessionStorage
 * يضمن بقاء المصدر (facebook, twitter, agent_id) محفوظاً حتى لو تنقل العميل بين الصفحات
 */
const URLTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get('source') || params.get('utm_source'); // دعم source العادي و utm_source
    const agentId = params.get('agent_id');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');

    if (source) {
      sessionStorage.setItem('userSource', source);
      logger.debug('🎯 Captured source from URL:', source);
    }

    // حفظ معلومات UTM الإضافية إذا وجدت لتحسين التتبع مستقبلاً
    if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
    if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);

    if (agentId) {
      sessionStorage.setItem('userSource', `agent_${agentId}`);
      sessionStorage.setItem('affiliateCountryCode', ''); // يمكن تحديثه لاحقاً
      logger.debug('🎯 Captured agent_id from URL:', agentId);
    }
  }, [location.search]);

  return null;
};

function App() {
  const location = useLocation();
  const hideToggleOnRoutes = ['/dashboard', '/chat/', '/subscription-details', '/ai-connect'];
  const hideLogoOnRoutes = ['/client/', '/dashboard', '/admin/', '/agent/', '/chat/', '/subscription-details', '/ai-connect']; const hideHeaderOnRoutes = ['/dashboard', '/chat/', '/subscription-details', '/reset-password', '/ai-connect', '/offers/'];

  const shouldHideHeader = hideHeaderOnRoutes.some(route => location.pathname.startsWith(route));
  const shouldHideToggle = hideToggleOnRoutes.some(route => location.pathname.startsWith(route));
  const shouldHideLogo = hideLogoOnRoutes.some(route => location.pathname.startsWith(route));

  return (
    <LanguageProvider>
      <HelmetProvider>
        <VisitorTrackerWrapper>
          <ScrollToTop />
          <AppWrapper>
            {!shouldHideLogo && <LogoLink href="/" title="Home" />}
            {!shouldHideHeader && <StickyHeader />}
            <LanguageToggleWrapper>
              {!shouldHideToggle && <LanguageToggle />}
            </LanguageToggleWrapper>

            {/* ✅ إضافة مكون غير مرئي لالتقاط معلمات التتبع عالمياً */}
            <URLTracker />

            {/* ✅ تغليف جميع الـ Routes بـ Suspense */}
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <>
                    <ConditionalElevenLabsChat agentId={process.env.REACT_APP_ELEVEN_LABS_AGENT_ID} />
                    <Home />
                  </>
                } />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/getting-started" element={<GettingStarted />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/subscription-details" element={<SubscriptionDetails />} />
                <Route path="/free-trial" element={<FreeTrial />} />

                {/* Country Code Routes */}
                <Route path="/:countryCode" element={<CountryAwareLayout />}>
                  <Route index element={
                    <>
                      <ConditionalElevenLabsChat agentId={process.env.REACT_APP_ELEVEN_LABS_AGENT_ID} />
                      <Home />
                    </>
                  } />
                  <Route path="about" element={<About />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<TermsOfService />} />
                  <Route path="data-deletion" element={<DataDeletion />} />
                  <Route path="getting-started" element={<GettingStarted />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="subscription-details" element={<SubscriptionDetails />} />
                </Route>

                {/* Client Routes */}
                <Route path="/login" element={<ClientLogin />} />
                <Route path="/reset-password" element={
  <PasswordResetForm
    endpoint="/api/v1/auth/reset-password"
    redirectPath="/login"
    titleAr="إعادة تعيين كلمة المرور"
    titleEn="Reset Password"
  />
} />

<Route path="/agent/set-password" element={
  <PasswordResetForm
    endpoint="/api/v1/agent/set-password"
    redirectPath="/agent/login"
    titleAr="تفعيل حساب الوكيل"
    titleEn="Activate Agent Account"
  />
} />

<Route path="/agent/reset-password" element={
  <PasswordResetForm
    endpoint="/api/v1/agent/reset-password"
    redirectPath="/agent/login"
    titleAr="إعادة تعيين كلمة مرور الوكيل"
    titleEn="Reset Agent Password"
  />
} />
                <Route path="/client/:uniqueLink" element={<ClientDashboard />} />
                <Route path="/dashboard" element={<ClientDashboard />} />

                <Route path="/:platform/success" element={<IntegrationSuccess />} />

                <Route path="/offers/:uniqueLink" element={<OfferPage />} />
                <Route path="/chat/:uniqueLink" element={<ChatPage />} />
                <Route path="/meta-callback" element={<MetaCallback />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/ai-connect" element={<ChatWidget />} />

                <Route path="/meta-auth-redirect" element={<MetaRedirectHandler />} />
                <Route path="/oauth/callback" element={<OAuthCallbackHandler />} />


                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="custom-offers" element={<CustomOffersManagement />} />
                  <Route path="custom-offers/create" element={<CustomOfferForm />} />
                  {/* ✅ Cohort Routes */}
                  <Route path="cohorts" element={<CohortManagement />} />
                  <Route path="cohorts/create" element={<CohortOfferForm />} />
                  <Route path="cohorts/:cohortId/seats" element={<CohortSeatsManager />} />
                  <Route path="cohorts/:cohortId/dashboard" element={<AdminDashboard />} />
                  <Route path="custom-offers/edit/:offerId" element={<CustomOfferForm />} />
                  <Route path="client-management" element={<ClientManagement />} />
                  <Route path="package-management" element={<PackageManagement />} />
                  <Route path="subscribers" element={<SubscribersList />} />
                  <Route path="management" element={<AdminManagement />} />
                  <Route path="roles" element={<RolesManagement />} />
                  <Route path="agents" element={<AgentsManagement />} />
                  <Route path="agent-applications" element={<AdminAgentApplicationsPage />} />
                  <Route path="agent-management" element={<AgentManagementPage />} />
                  <Route path="payouts" element={<AgentPayoutsPage />} />
                  <Route path="payout-requests" element={<PayoutRequestsPage />} />
                  <Route path="monthly-reports" element={<MonthlyReportsPage />} />
                  <Route path="agent-performance" element={<AgentPerformancePage />} />
                  <Route path="financials/agent/:agentId" element={<FinancialManagementPage />} />
                  <Route path="subscription-analytics" element={<SubscriptionAnalyticsPage />} />
                  <Route path="monitoring-dashboard" element={<MonitoringDashboard />} />
                  <Route path="online-users" element={<OnlineUsers />} />
                  <Route path="lead-tracking" element={<AdminLeadTrackingPage />} />
                </Route>
                <Route path="/offer/:slug" element={<CustomOfferPage />} />
                <Route path="/cohort/:slug" element={<CohortOfferPage />} />
                <Route path="/join-agent" element={<JoinAgentPage />} />

                {/* Agent Routes */}
                <Route path="/agent/login" element={<AgentLogin />} />
                <Route path="/agent" element={<AgentLayout />}>
                  <Route index element={<AgentDashboard />} />
                  <Route path="dashboard" element={<AgentDashboard />} />
                  <Route path="clients" element={<AgentClientManagement />} />
                  <Route path="leads" element={<AgentLeads />} />
                  <Route path="earnings" element={<AgentEarningsPage />} />
                  <Route path="offers" element={<AgentOfferManagement />} />
                </Route>

                {/* 404 */}
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
          </AppWrapper>
        </VisitorTrackerWrapper>
      </HelmetProvider>
    </LanguageProvider>
  );
}

export default App;